import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft, RefreshCw, MessageSquarePlus, ChevronRight,
    Phone, MessageCircle, Mail, FileText, Info, Loader2,
} from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { ContainerCard } from "../ui/cards";
import { Button } from "../ui/button";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../common/ToastMsg";
import {
    ENGAGE_ACTIVITY_LOGS_URL,
    ENGAGE_ACTIVITY_LOG_URL,
} from "../../constants/api";

dayjs.extend(relativeTime);

const TABS = ['All Activities', 'Status', 'Notes', 'Calls', 'WhatsApp', 'Email', 'Documents'];

const LOG_TYPE_ICON = {
    status:   Info,
    note:     MessageSquarePlus,
    call:     Phone,
    whatsapp: MessageCircle,
    sms:      MessageCircle,
    email:    Mail,
    document: FileText,
    system:   Info,
};

const LOG_TYPE_BADGE = {
    status:   'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300',
    note:     'bg-amber-100  text-amber-800  dark:bg-amber-950/40  dark:text-amber-300',
    call:     'bg-blue-100   text-blue-700   dark:bg-blue-950/40   dark:text-blue-300',
    whatsapp: 'bg-green-100  text-green-700  dark:bg-green-950/40  dark:text-green-300',
    sms:      'bg-cyan-100   text-cyan-700   dark:bg-cyan-950/40   dark:text-cyan-300',
    email:    'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300',
    document: 'bg-slate-100  text-slate-700  dark:bg-slate-800     dark:text-slate-300',
    system:   'bg-gray-100   text-gray-700   dark:bg-gray-800      dark:text-gray-400',
};

// ────────────────────────────────────────────────────────────────────────────
// Timeline card
// ────────────────────────────────────────────────────────────────────────────

const LogEntry = ({ log, onDelete }) => {
    const Icon = LOG_TYPE_ICON[log.log_type] || Info;
    const badgeCls = LOG_TYPE_BADGE[log.log_type] || LOG_TYPE_BADGE.system;

    return (
        <div className="flex gap-3">
            <div className="flex flex-col items-center pt-1">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center ${badgeCls}`}>
                    <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 w-px bg-gray-200 dark:bg-gray-700 mt-1" />
            </div>
            <div className="flex-1 pb-6">
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span className="capitalize font-medium">{log.log_type_display || log.log_type}</span>
                    <span>·</span>
                    <span title={dayjs(log.created_date).format('YYYY-MM-DD HH:mm')}>
                        {dayjs(log.created_date).format('MMM D, YYYY · h:mm A')}
                    </span>
                    {log.created_by_name && (<><span>·</span><span>{log.created_by_name}</span></>)}
                    {onDelete && log.log_type !== 'status' && (
                        <button
                            onClick={() => onDelete(log)}
                            className="ml-auto text-gray-400 hover:text-red-500 text-xs"
                        >
                            Delete
                        </button>
                    )}
                </div>

                {log.log_type === 'status' ? (
                    <div className="mt-1 flex items-center gap-2 text-sm">
                        <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs">
                            {log.from_status || '—'}
                        </span>
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                        <span className="px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 text-xs font-medium">
                            {log.to_status}
                        </span>
                    </div>
                ) : (
                    log.message && (
                        <div className="mt-1 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                            {log.message}
                        </div>
                    )
                )}
                {log.follow_up_date && (
                    <div className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                        Follow-up: {dayjs(log.follow_up_date).format('MMM D, YYYY')}
                    </div>
                )}
            </div>
        </div>
    );
};

// ────────────────────────────────────────────────────────────────────────────
// Inline note composer
// ────────────────────────────────────────────────────────────────────────────

const NoteComposer = ({ onSubmit, busy }) => {
    const [logType, setLogType] = useState('note');
    const [message, setMessage] = useState('');
    const [followUp, setFollowUp] = useState('');

    const canSubmit = message.trim().length > 0 && !busy;

    const submit = async () => {
        if (!canSubmit) return;
        await onSubmit({
            log_type: logType,
            message: message.trim(),
            follow_up_date: followUp || null,
        });
        setMessage('');
        setFollowUp('');
    };

    return (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900">
            <div className="flex gap-2 mb-2 flex-wrap">
                {['note', 'call', 'whatsapp', 'email', 'sms'].map(t => (
                    <button
                        key={t}
                        onClick={() => setLogType(t)}
                        className={`px-2 py-1 text-xs rounded-full border capitalize
                          ${logType === t
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                    >
                        {t}
                    </button>
                ))}
            </div>
            <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Add a note, call summary, or comment..."
                rows={3}
                className="w-full text-sm border border-gray-200 dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            />
            <div className="flex items-center gap-2 mt-2">
                <label className="text-xs text-gray-500 dark:text-gray-400">Follow-up</label>
                <input
                    type="date"
                    value={followUp}
                    onChange={(e) => setFollowUp(e.target.value)}
                    className="text-xs border border-gray-200 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-900"
                />
                <Button
                    className="ml-auto"
                    onClick={submit}
                    disabled={!canSubmit}
                >
                    {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                    Add
                </Button>
            </div>
        </div>
    );
};

// ────────────────────────────────────────────────────────────────────────────
// Info card row
// ────────────────────────────────────────────────────────────────────────────

const InfoRow = ({ label, value, mono }) => (
    <div className="flex justify-between items-start gap-4 py-1.5 text-sm border-b border-gray-100 dark:border-gray-800 last:border-0">
        <span className="text-gray-500 dark:text-gray-400 shrink-0">{label}</span>
        <span className={`text-gray-900 dark:text-gray-100 text-right ${mono ? 'font-mono text-xs' : ''}`}>
            {value ?? <span className="text-gray-400">—</span>}
        </span>
    </div>
);

// ────────────────────────────────────────────────────────────────────────────
// Shell
// ────────────────────────────────────────────────────────────────────────────
//
// Props:
//   entityType    'enquiry' | 'patient'
//   entityId      number
//   entity        the loaded record (or null while loading)
//   loading       bool — while entity loads
//   onRefresh     () => refetch entity
//   title         string — big header
//   subtitle      string — small line under title
//   badges        [{ label, className }]
//   infoRows      [{ label, value, mono? }] — left-panel content
//   statusOptions [{ value, label }] — for the change-status dropdown; omit to hide
//   currentStatus string
//   onChangeStatus  (newStatus) => Promise
//   sideExtras    optional React node rendered below the info card
//   actions       [{ label, onClick, icon }] — bottom-of-info-card buttons
export const EntityDetailShell = ({
    entityType,
    entityId,
    entity,
    loading,
    onRefresh,
    title,
    subtitle,
    badges = [],
    infoRows = [],
    statusOptions,
    currentStatus,
    onChangeStatus,
    sideExtras,
    actions = [],
}) => {
    const navigate = useNavigate();
    const { fetch: apiFetch, create, remove } = useApiMethods({ throwOnFail: true });

    const [logs, setLogs]         = useState([]);
    const [logsLoading, setLogsLoading] = useState(true);
    const [tab, setTab]           = useState('All Activities');
    const [posting, setPosting]   = useState(false);
    const [changingStatus, setChangingStatus] = useState(false);

    const filterQuery = useMemo(
        () => ({ [entityType]: entityId }),
        [entityType, entityId],
    );

    const loadLogs = () => {
        if (!entityId) return;
        setLogsLoading(true);
        apiFetch(ENGAGE_ACTIVITY_LOGS_URL, filterQuery)
            .then((res) => setLogs(res?.results || []))
            .catch(() => ToastMsg('error', 'Failed to load activity'))
            .finally(() => setLogsLoading(false));
    };

    useEffect(() => { loadLogs(); /* eslint-disable-next-line */ }, [entityId, entityType]);

    const postNote = async (payload) => {
        setPosting(true);
        try {
            const body = { ...payload, [entityType]: entityId };
            await create(ENGAGE_ACTIVITY_LOGS_URL, body);
            ToastMsg('success', `${payload.log_type} logged`);
            loadLogs();
        } catch (e) {
            ToastMsg('error', 'Failed to post');
        } finally {
            setPosting(false);
        }
    };

    const deleteLog = async (log) => {
        try {
            await remove(ENGAGE_ACTIVITY_LOG_URL(log.id));
            loadLogs();
        } catch { ToastMsg('error', 'Failed to delete'); }
    };

    const handleStatusChange = async (newStatus) => {
        if (!onChangeStatus || newStatus === currentStatus) return;
        setChangingStatus(true);
        try {
            await onChangeStatus(newStatus);
            loadLogs();
        } finally {
            setChangingStatus(false);
        }
    };

    const filteredLogs = useMemo(() => {
        if (tab === 'All Activities') return logs;
        if (tab === 'Status')     return logs.filter(l => l.log_type === 'status');
        if (tab === 'Notes')      return logs.filter(l => l.log_type === 'note');
        if (tab === 'Calls')      return logs.filter(l => l.log_type === 'call');
        if (tab === 'WhatsApp')   return logs.filter(l => l.log_type === 'whatsapp' || l.log_type === 'sms');
        if (tab === 'Email')      return logs.filter(l => l.log_type === 'email');
        if (tab === 'Documents')  return logs.filter(l => l.log_type === 'document');
        return logs;
    }, [logs, tab]);

    return (
        <div className="p-4 space-y-4">
            {/* Header */}
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                    <ArrowLeft className="h-4 w-4" />
                </button>
                <div className="flex-1">
                    <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {loading ? 'Loading…' : title}
                    </h1>
                    {subtitle && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>
                    )}
                </div>
                <Button variant="outline" onClick={onRefresh}>
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Refresh
                </Button>
            </div>

            {/* Body: left info card + right timeline */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* ── Left column ─────────────────────────────────────── */}
                <div className="space-y-4">
                    <ContainerCard>
                        <div className="p-4">
                            <div className="text-xs uppercase text-gray-400 mb-2 tracking-wide">
                                {entityType === 'enquiry' ? 'Enquiry information' : 'Patient information'}
                            </div>
                            <div className="flex flex-wrap gap-1.5 mb-3">
                                {badges.map((b, i) => (
                                    <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${b.className}`}>
                                        {b.label}
                                    </span>
                                ))}
                            </div>
                            <div>
                                {infoRows.map((r, i) => (
                                    <InfoRow key={i} label={r.label} value={r.value} mono={r.mono} />
                                ))}
                            </div>

                            {statusOptions && (
                                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                                    <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                                        Change status
                                    </label>
                                    <select
                                        value={currentStatus || ''}
                                        onChange={(e) => handleStatusChange(e.target.value)}
                                        disabled={changingStatus}
                                        className="w-full text-sm border border-gray-200 dark:border-gray-700 rounded px-2 py-1.5 bg-white dark:bg-gray-900"
                                    >
                                        {statusOptions.map(o => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {actions.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex flex-wrap gap-2">
                                    {actions.map((a, i) => (
                                        <Button key={i} variant="outline" onClick={a.onClick}>
                                            {a.icon ? <a.icon className="h-3.5 w-3.5 mr-1" /> : null}
                                            {a.label}
                                        </Button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </ContainerCard>

                    {sideExtras}
                </div>

                {/* ── Right column: tabs + timeline ──────────────────── */}
                <div className="lg:col-span-2 space-y-4">
                    <ContainerCard>
                        <div className="p-4">
                            {/* Tab bar */}
                            <div className="flex flex-wrap gap-1 border-b border-gray-200 dark:border-gray-800 -mx-4 px-4 pb-0">
                                {TABS.map(t => (
                                    <button
                                        key={t}
                                        onClick={() => setTab(t)}
                                        className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px
                                          ${tab === t
                                            ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                                            : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                                          }`}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>

                            {/* Composer */}
                            <div className="mt-4">
                                <NoteComposer onSubmit={postNote} busy={posting} />
                            </div>

                            {/* Timeline */}
                            <div className="mt-4">
                                {logsLoading ? (
                                    <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
                                        <Loader2 className="h-4 w-4 animate-spin" /> Loading activity…
                                    </div>
                                ) : filteredLogs.length === 0 ? (
                                    <div className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
                                        No {tab.toLowerCase()} yet.
                                    </div>
                                ) : (
                                    <div className="space-y-0">
                                        {filteredLogs.map(l => (
                                            <LogEntry key={l.id} log={l} onDelete={deleteLog} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </ContainerCard>
                </div>
            </div>
        </div>
    );
};

export default EntityDetailShell;
